import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Clock, History, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

const formatarData = (data) => {
  if (!data) return "-";
  return new Date(data).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const calcularTempo = (dataInicio, dataFim) => {
  if (!dataInicio) return "N/A";
  const inicio = new Date(dataInicio);
  const fim = dataFim ? new Date(dataFim) : new Date();
  const diffMs = Math.max(0, fim - inicio);

  const minutos = Math.floor((diffMs / (1000 * 60)) % 60);
  const horas = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (dias > 0) return `${dias}d ${horas}h ${minutos}m`;
  if (horas > 0) return `${horas}h ${minutos}m`;
  return `${minutos}m`;
};

function buildTimelineEntries(historicoTags) {
  if (!historicoTags || historicoTags.length === 0) return [];

  const entries = [];
  for (let i = 0; i < historicoTags.length; i++) {
    const current = historicoTags[i];
    const next = historicoTags[i + 1];
    entries.push({
      tag_id: current.tag_nova_id,
      tag_nome: current.tag_nova_nome,
      entrada: current.data,
      saida: next ? next.data : null,
      isAtual: !next,
    });
  }
  return entries;
}

function buildContractTimelineEntries(historicoTags) {
  if (!historicoTags || historicoTags.length === 0) return [];
  return historicoTags.map((entry, i) => ({
    tag_id: entry.tag_id,
    tag_nome: entry.tag_nome,
    acao: entry.acao,
    data: entry.data,
    isLast: i === historicoTags.length - 1,
  }));
}

export function TimelineTagsModal({
  open,
  onOpenChange,
  entity,
  tags,
  mode = "cliente",
}) {
  const isCliente = mode === "cliente";
  const historicoTags = entity?.historico_tags || [];
  const tagAtualNome = isCliente
    ? entity?.tag_gestao_nome
    : null;
  const tagAtualId = isCliente ? entity?.tag_gestao_id : null;

  const tagMap = useMemo(() => {
    const m = {};
    (tags || []).forEach((t) => {
      m[t.id] = t;
    });
    return m;
  }, [tags]);

  const getTagColor = (tagId) => {
    const t = tagMap[tagId];
    if (t) return { cor: t.cor, bgCor: t.bgCor };
    return { cor: "#6B7280", bgCor: "#F3F4F6" };
  };

  const timelineEntries = isCliente
    ? buildTimelineEntries(historicoTags)
    : buildContractTimelineEntries(historicoTags);

  const resumoTempo = useMemo(() => {
    if (!isCliente || timelineEntries.length === 0) return [];
    const tempos = {};
    const nomes = {};
    const cores = {};
    timelineEntries.forEach((entry) => {
      const ms = entry.saida
        ? new Date(entry.saida) - new Date(entry.entrada)
        : new Date() - new Date(entry.entrada);
      tempos[entry.tag_id] = (tempos[entry.tag_id] || 0) + Math.max(0, ms);
      nomes[entry.tag_id] = entry.tag_nome;
      cores[entry.tag_id] = getTagColor(entry.tag_id);
    });
    return Object.entries(tempos).map(([id, ms]) => ({
      id,
      nome: nomes[id],
      tempoMs: ms,
      cor: cores[id],
    }));
  }, [timelineEntries, isCliente, tagMap]);

  if (!entity) return null;

  const entityName = entity.nome || entity.cliente_nome || "N/A";
  const entityCpf = entity.cpf || entity.cliente_cpf || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="timeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-blue-600" />
            Linha do Tempo - {isCliente ? "Esteiras" : "Tags do Contrato"}
          </DialogTitle>
          <DialogDescription>
            {entityName}
            {entityCpf && (
              <>
                {" "}- CPF: {entityCpf}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Tag Atual - only for clients */}
          {isCliente && tagAtualNome && (
            <div
              className="p-4 rounded-xl border-2"
              style={{
                backgroundColor: getTagColor(tagAtualId).bgCor,
                borderColor: getTagColor(tagAtualId).cor,
              }}
              data-testid="timeline-tag-atual"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: getTagColor(tagAtualId).cor }}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Esteira Atual
                    </p>
                    <p
                      className="text-lg font-bold"
                      style={{ color: getTagColor(tagAtualId).cor }}
                    >
                      {tagAtualNome}
                    </p>
                  </div>
                </div>
                <Badge className="bg-blue-600 hover:bg-blue-600 text-white">
                  Atual
                </Badge>
              </div>
            </div>
          )}

          {/* Current contract tags */}
          {!isCliente && entity.tags_gestao_nomes?.length > 0 && (
            <div className="p-4 rounded-xl border-2 border-blue-200 bg-blue-50" data-testid="timeline-tags-atuais-contrato">
              <p className="text-sm font-medium text-gray-500 mb-2">
                Tags Atuais do Contrato
              </p>
              <div className="flex flex-wrap gap-2">
                {entity.tags_gestao_nomes.map((nome, i) => {
                  const tagId = entity.tags_gestao?.[i];
                  const colors = getTagColor(tagId);
                  return (
                    <Badge
                      key={i}
                      style={{
                        backgroundColor: colors.bgCor,
                        color: colors.cor,
                        borderColor: colors.cor,
                      }}
                      variant="outline"
                      className="text-sm font-semibold"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full mr-1.5"
                        style={{ backgroundColor: colors.cor }}
                      />
                      {nome}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Historico */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Historico de Movimentacoes
            </h4>

            {timelineEntries.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl" data-testid="timeline-empty">
                <History className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Sem historico registrado</p>
                {entity.created_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    Cadastrado em: {formatarData(entity.created_at)}
                  </p>
                )}
              </div>
            ) : isCliente ? (
              <ClienteTimeline
                entries={timelineEntries}
                getTagColor={getTagColor}
              />
            ) : (
              <ContratoTimeline
                entries={timelineEntries}
                getTagColor={getTagColor}
              />
            )}
          </div>

          {/* Resumo - only for clients */}
          {isCliente && resumoTempo.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-xl" data-testid="timeline-resumo">
              <h4 className="font-semibold text-gray-700 mb-3">
                Resumo do Tempo por Esteira
              </h4>
              <div className="space-y-2.5">
                {resumoTempo.map((item) => {
                  const dias = Math.floor(
                    item.tempoMs / (1000 * 60 * 60 * 24)
                  );
                  const horas = Math.floor(
                    (item.tempoMs / (1000 * 60 * 60)) % 24
                  );
                  const minutos = Math.floor(
                    (item.tempoMs / (1000 * 60)) % 60
                  );
                  const tempoStr =
                    dias > 0
                      ? `${dias}d ${horas}h ${minutos}m`
                      : `${horas}h ${minutos}m`;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.cor.cor }}
                        />
                        <span className="text-sm">{item.nome}</span>
                      </div>
                      <span className="font-mono text-sm font-semibold text-gray-700">
                        {tempoStr}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClienteTimeline({ entries, getTagColor }) {
  return (
    <div className="relative" data-testid="timeline-cliente-entries">
      {/* Vertical line */}
      <div className="absolute left-[18px] top-3 bottom-3 w-0.5 bg-gray-200" />

      <div className="space-y-4">
        {entries.map((entry, index) => {
          const colors = getTagColor(entry.tag_id);
          const tempo = calcularTempo(entry.entrada, entry.saida);

          return (
            <div key={index} className="relative pl-12" data-testid={`timeline-entry-${index}`}>
              {/* Circle on line */}
              <div
                className={cn(
                  "absolute left-2 top-3 w-[22px] h-[22px] rounded-full border-[3px] bg-white z-10",
                  entry.isAtual && "ring-2 ring-blue-300 ring-offset-1"
                )}
                style={{ borderColor: colors.cor }}
              >
                <div
                  className="w-full h-full rounded-full scale-[0.6]"
                  style={{ backgroundColor: colors.cor }}
                />
              </div>

              {/* Card */}
              <div
                className={cn(
                  "p-4 rounded-xl border transition-all",
                  entry.isAtual
                    ? "border-blue-200 bg-blue-50/70 shadow-sm"
                    : "border-gray-200 bg-white hover:shadow-sm"
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="font-bold text-base"
                    style={{ color: colors.cor }}
                  >
                    {entry.tag_nome}
                  </span>
                  {entry.isAtual && (
                    <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-xs">
                      Atual
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-400 uppercase tracking-wider text-[10px]">
                      Entrada
                    </span>
                    <p className="font-semibold text-gray-700 mt-0.5">
                      {formatarData(entry.entrada)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 uppercase tracking-wider text-[10px]">
                      Saida
                    </span>
                    <p className="font-semibold text-gray-700 mt-0.5">
                      {entry.saida ? formatarData(entry.saida) : "---"}
                    </p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-600">
                    Tempo: {tempo}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContratoTimeline({ entries, getTagColor }) {
  return (
    <div className="relative" data-testid="timeline-contrato-entries">
      <div className="absolute left-[18px] top-3 bottom-3 w-0.5 bg-gray-200" />

      <div className="space-y-3">
        {entries.map((entry, index) => {
          const colors = getTagColor(entry.tag_id);
          const isAdd = entry.acao === "ADICIONADA";

          return (
            <div key={index} className="relative pl-12" data-testid={`timeline-contrato-entry-${index}`}>
              <div
                className="absolute left-2 top-3 w-[22px] h-[22px] rounded-full border-[3px] bg-white z-10"
                style={{ borderColor: isAdd ? colors.cor : "#EF4444" }}
              >
                <div
                  className="w-full h-full rounded-full scale-[0.6]"
                  style={{
                    backgroundColor: isAdd ? colors.cor : "#EF4444",
                  }}
                />
              </div>

              <div className="p-3 rounded-xl border border-gray-200 bg-white hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="text-xs font-semibold"
                      style={{
                        backgroundColor: isAdd ? colors.bgCor : "#FEF2F2",
                        color: isAdd ? colors.cor : "#EF4444",
                        borderColor: isAdd ? colors.cor : "#EF4444",
                      }}
                    >
                      {isAdd ? "+" : "-"} {entry.tag_nome}
                    </Badge>
                    <span className="text-xs text-gray-400">
                      {isAdd ? "Adicionada" : "Removida"}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-500">
                    {formatarData(entry.data)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
